import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp, Flex, Text, useI18n } from '@pega/cosmos-react-core';
import BlockQuote from '@pega/cosmos-react-rte/lib/components/RichTextEditor/DecoratorComponents/BlockQuote';
import Code from '@pega/cosmos-react-rte/lib/components/RichTextEditor/DecoratorComponents/Code';
import CodeBlock from '@pega/cosmos-react-rte/lib/components/RichTextEditor/DecoratorComponents/CodeBlock';

interface MarkdownSyntaxSection {
  title: 'styles' | 'images' | 'links' | 'lists' | 'headings' | 'code';
  content: ReactNode;
}

const StyledSectionHeading = styled(Text)(({ theme }) => {
  return css`
    padding-block: ${theme.base.spacing};
  `;
});

StyledSectionHeading.defaultProps = defaultThemeProp;

const BoldText = styled.span(({ theme }) => {
  return css`
    font-weight: ${theme.base['font-weight'].bold};
  `;
});

BoldText.defaultProps = defaultThemeProp;

const ItalicText = styled.span`
  font-style: italic;
`;

const IndentedText = styled.span(({ theme }) => {
  return css`
    margin-inline-start: calc(2 * ${theme.base.spacing});
  `;
});

IndentedText.defaultProps = defaultThemeProp;

const StyledCode = styled(Code)`
  width: fit-content;
`;

const StyledCodeBlock = styled(CodeBlock)`
  display: flex;
  flex-direction: column;
`;

const MarkdownSyntax = () => {
  const t = useI18n();

  const sections: MarkdownSyntaxSection[] = [
    {
      title: 'styles',
      content: (
        <>
          <BoldText>**{t('markdown_bold')}**</BoldText>
          <ItalicText>*{t('markdown_italic')}*</ItalicText>
          <BlockQuote>{`> ${t('markdown_blockquote')}`}</BlockQuote>
        </>
      )
    },
    {
      title: 'images',
      content: (
        <span>
          ![{t('markdown_alt_text')}]({t('markdown_image_url')})
        </span>
      )
    },
    {
      title: 'links',
      content: (
        <span>
          [{t('markdown_link_text')}]({t('markdown_link_url')})
        </span>
      )
    },
    {
      title: 'lists',
      content: (
        <>
          <span>1. {t('markdown_ordered_list')}</span>
          <IndentedText>1. {t('markdown_ordered_sublist')}</IndentedText>
          <span>* {t('markdown_unordered_list')}</span>
          <IndentedText>* {t('markdown_unordered_sublist')}</IndentedText>
        </>
      )
    },
    {
      title: 'headings',
      content: (
        <>
          <Text variant='h1' as='span'>
            # {t('markdown_heading_level', ['1'])}
          </Text>
          <Text variant='h2' as='span'>
            ## {t('markdown_heading_level', ['2'])}
          </Text>
          <Text variant='h3' as='span'>
            ### {t('markdown_heading_level', ['3'])}
          </Text>
          <Text variant='h4' as='span'>
            #### {t('markdown_heading_level', ['4'])}
          </Text>
        </>
      )
    },
    {
      title: 'code',
      content: (
        <>
          <StyledCode>`{t('markdown_code')}`</StyledCode>
          <StyledCodeBlock>
            <span>```</span>
            <span>{t('markdown_code_block')}</span>
            <span>```</span>
          </StyledCodeBlock>
        </>
      )
    }
  ];

  return (
    <Flex container={{ direction: 'column', gap: 1 }}>
      <span>{t('markdown_instructions')}</span>
      {sections.map(section => (
        <div key={section.title}>
          <StyledSectionHeading variant='h3'>{t(`markdown_${section.title}`)}</StyledSectionHeading>
          <Flex container={{ direction: 'column', gap: 0.5, pad: [1, 0, 0, 2] }}>
            {section.content}
          </Flex>
        </div>
      ))}
    </Flex>
  );
};

export default MarkdownSyntax;
